(ns ui.editor2.sidebar-hyperframes-templates
  "The Templates entry of the editor2 sidebar: the catalog panel that
   adds a HyperFrames template as clip, and the variables panel of a
   selected template clip. editor2 opens the sidebar panel named like
   the asset type (`:hyperframes`) when a clip is selected, see
   `ui.editor2.actions/set-sidebar`."
  (:require [datascript.core :as ds]
            [parts.replicant.query :as query]
            [ui.dark-launch :as dark-launch]
            [ui.hyperframes-templates.player :as player]))

(def catalog-query
  {:query/kind :query/hyperframes-templates})

(def entry-class
  ["flex" "pt-3" "pb-3" "pl-2" "pr-2" "flex-col" "justify-center" "items-center"
   "gap-1" "rounded-lg" "cursor-pointer" "hover:bg-gray-600"])

(defn icon
  [selected?]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :fill "none"
         :viewBox "0 0 24 24"
         :stroke-width "1.5"
         :stroke "currentColor"
         :class ["size-6" (when-not selected? "text-slate-400")]}
   [:path {:stroke-linecap "round"
           :stroke-linejoin "round"
           :d "M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z"}]])

(defn entry-button
  [selected?]
  [:div
   {:class (conj entry-class (when selected? "bg-gray-800"))
    :on {:click [[:store/assoc-in [:editor2 :sidebar-panel] :hyperframes-templates]
                 [:data/query catalog-query]]}}
   (icon selected?)
   [:span
    {:class ["text-center" "text-xs" "font-medium" (when-not selected? "text-slate-400")]}
    "Templates"]])

(defn entry
  "Only while the feature is switched on in this browser, see
   `ui.hyperframes-templates.devtools`."
  [selected?]
  (when (dark-launch/enabled? :hyperframes-templates)
    (entry-button selected?)))

(defn templates
  [state]
  (query/get-result state catalog-query))

(defn load-catalog-on-mount
  "Runs the catalog query when the panel opens without it, e.g. a
   template clip of a loaded design is selected."
  [state]
  (fn [_]
    (when-not (templates state)
      ((:replicant/dispatch! state) {:actions [[:data/query catalog-query]]}))))

(def preview-style
  {:display "block"
   :width "100%"
   :height "100%"
   :pointer-events "none"})

(defn template-card
  [template]
  [:div
   {:replicant/key (:template/id template)
    :class ["flex" "flex-col" "gap-1" "p-1" "rounded-lg" "cursor-pointer" "hover:bg-gray-700"]
    :on {:click [[:hyperframes/add-template template]]}}
   [:div
    {:class ["rounded-md" "overflow-hidden" "bg-gray-900"]
     :style {:aspect-ratio "9 / 16"}}
    (player/element {:template-id (:template/id template)
                     :width (:template/width template)
                     :height (:template/height template)
                     :loop? true
                     :style preview-style})]
   [:span
    {:class ["text-xs" "text-center" "text-slate-300"]}
    (:template/name template)]])

(defn panel
  [state]
  [:div
   {:class ["flex" "flex-col" "gap-3" "w-full"]
    :replicant/on-mount (load-catalog-on-mount state)}
   [:span {:class ["text-xs" "font-medium"]} "Templates"]
   (if (query/loading? state catalog-query)
     [:div {:class ["text-xs" "text-slate-400"]} "Loading..."]
     (into [:div {:class ["grid" "grid-cols-2" "gap-2"]}]
           (map template-card
                (templates state))))])

(def input-class
  ["w-full" "rounded" "border-gray-900" "text-white" "shadow-sm" "text-sm"])

(def input-style
  {:background-color "oklch(0.21 0.034 264.665)"})

(defn variable-input
  [asset-uuid values {:keys [id type label options] :as variable}]
  (let [value (get values id (:default variable))
        on-input [[:hyperframes/set-variable asset-uuid id type :event/target.value]]]
    [:label
     {:replicant/key id
      :class ["flex" "flex-col" "gap-1"]}
     [:span {:class ["text-xs" "font-medium"]} label]
     (case type
       "color"
       [:input {:type "color"
                :class ["h-9" "w-16" "rounded" "border-gray-900" "bg-transparent"]
                :value value
                :on {:input on-input}}]

       "number"
       [:input {:type "number"
                :class input-class
                :style input-style
                :value value
                :min (:min variable)
                :max (:max variable)
                :step (:step variable)
                :on {:input on-input}}]

       "enum"
       (into [:select {:class input-class
                       :style input-style
                       :value value
                       :on {:change on-input}}]
             (map (fn [option]
                    [:option {:value (:value option)}
                     (:label option)])
                  options))

       ;; string, boolean and unknown types edit as text:
       [:input {:type "text"
                :class input-class
                :style input-style
                :value (str value)
                :maxLength (:maxLength variable)
                :on {:input on-input}}])]))

(defn selected-clip
  [state]
  (when-let [clip-uuid (get-in state [:editor2 :selected-clip])]
    (ds/entity (get-in state [:editor2 :db])
               [:clip/uuid clip-uuid])))

(defn find-template
  [state template-id]
  (some (fn [template]
          (when (= (:template/id template)
                   template-id)
            template))
        (templates state)))

(defn variables-panel
  [state]
  (let [asset (:clip/asset (selected-clip state))
        data (:asset/data asset)
        template (find-template state (:hyperframes/template-id data))]
    [:div
     {:class ["flex" "flex-col" "gap-3" "w-full"]
      :replicant/on-mount (load-catalog-on-mount state)}
     [:div
      {:class ["flex" "items-center" "justify-between"]}
      [:span {:class ["text-xs" "font-medium"]}
       (or (:template/name template) "Template")]
      [:button
       {:type "button"
        :class ["text-xs" "text-slate-400" "hover:text-white"]
        :on {:click [[:store/assoc-in [:editor2 :sidebar-panel] :hyperframes-templates]
                     [:data/query catalog-query]]}}
       "All templates"]]
     (if template
       (into [:div {:class ["flex" "flex-col" "gap-3"]}]
             (map (partial variable-input
                           (:asset/uuid asset)
                           (:hyperframes/variables data))
                  (:template/variables template)))
       [:div {:class ["text-xs" "text-slate-400"]} "Loading..."])]))
